/**
 * The host operators: who may sign in to the host, what each may do on the host's own pages, and which
 * organizations each may open - as an administrator or as a technician.
 *
 *   * NEEDS "MANAGE OPERATORS". Everything here is refused server-side without it, so the rail hides
 *     the entry and a deep link renders a sentence instead of an empty list.
 *   * NO ESCALATION. An operator can hand out only the permissions and organizations they hold, and
 *     can change only operators who hold no more than they do. Rows they cannot change say so rather
 *     than offering buttons the server would refuse.
 *   * NOBODY CHANGES THEIR OWN ACCESS, and the last operator with every permission and every
 *     organization cannot lose either - otherwise a mistake here could only be undone from DSM.
 *   * ONE EDITOR AND ONE PASSWORD FORM, not one per row, as on the organization's own Users page:
 *     rows are rebuilt on every refresh, which would discard a half-typed password.
 */
import type { CheckBox, ComboBox, Container, StackPanel } from 'webwin';
import { currentUser } from '../api/auth.js';
import * as host from '../api/host.js';
import * as hostAccess from '../api/hostAccess.js';
import { describeError } from '../api/http.js';
import {
  HostPermission,
  type HostTenantAssignment,
  type HostUserDto,
  type TenantDto
} from '../contracts.gen.js';
import { covers, organizationSummary, permissionSummary, PERMISSIONS } from '../hostUsers.js';
import { join, localDate, localDateTime, plural } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  caption,
  checkLine,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  inlineRow,
  muted,
  passwordBox,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const ROLES = [
  { admin: true, label: 'Administrator' },
  { admin: false, label: 'Technician' }
] as const;

/** One organization row in the editor. */
interface OrgRow {
  tenant: TenantDto;
  check: CheckBox;
  role: ComboBox<(typeof ROLES)[number]>;
}

export function hostUsersPage(): CrmPage {
  return {
    mount(host_: Container, ctx: PageContext) {
      const { view, content } = scrollPage();
      host_.controls.add(view);

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Users'),
        muted('The people who sign in to the host: what each may do here, and which organizations each may open.')
      );
      content.controls.add(header);

      if (hostAccess.isLoaded() && !hostAccess.has(HostPermission.ManageUsers)) {
        const notice = card();
        notice.controls.add(
          text('Managing host operators needs the Manage operators permission. To change your own password, use Change password in the sidebar.')
        );
        content.controls.add(notice);
        relayout(content);
        return;
      }

      let busy = false;
      let tenants: TenantDto[] = [];
      const status = new StatusLine();
      header.controls.add(stackOf(buttonRow(), button('Add operator', () => openEditor(null), true), button('Refresh', () => void load())));
      header.controls.add(status.label);

      // ---- add / edit ----
      let editing: HostUserDto | null = null;
      const editorTitle = h2('Add operator');
      const nameBox = textBox({ placeholder: 'Their name' });
      const emailBox = textBox({ placeholder: 'name@example.com' });
      const emailField = field('Email', emailBox);
      const tempPassword = passwordBox();
      const passwordField = stackOf(
        vstack({ spacing: GAP.xs }),
        field('Temporary password', tempPassword),
        muted('They choose their own password at first sign-in.')
      );

      const permChecks = PERMISSIONS.map(p => ({ flag: p.flag, check: wrappingCheck(`${p.label} - ${p.hint}`) }));
      const permissions = stackOf(
        vstack({ spacing: GAP.xs }),
        caption('Host permissions'),
        ...permChecks.map(p => checkLine(p.check)),
        muted('Every operator sees the dashboard and can open the organizations below.')
      );

      const allOrgs = wrappingCheck('Every organization, including ones created later, as administrator');
      const orgRowsPanel: StackPanel = vstack({ spacing: GAP.xs });
      const orgHint = muted('');
      let orgRows: OrgRow[] = [];
      const organizations = stackOf(vstack({ spacing: GAP.xs }), caption('Organizations'), checkLine(allOrgs), orgRowsPanel, orgHint);
      allOrgs.on('change', () => showOrgRows());

      const editorStatus = new StatusLine();
      const saveButton = button('Add', () => void save(), true);
      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('Name', nameBox), emailField),
          passwordField,
          permissions,
          organizations,
          stackOf(buttonRow(), saveButton, button('Cancel', () => closeEditor())),
          editorStatus.label
        )
      );

      // ---- reset password ----
      let resetting: HostUserDto | null = null;
      const resetTitle = h2('Reset password');
      const newPassword = passwordBox();
      const confirmPassword = passwordBox();
      const resetStatus = new StatusLine();
      const resetCard = card({ visible: false });
      resetCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          resetTitle,
          fieldRow(field('New password', newPassword), field('Confirm', confirmPassword)),
          muted('Every session of theirs is signed out, a sign-in lockout on the address is released, and they choose their own password at next sign-in.'),
          stackOf(buttonRow(), button('Reset password', () => void resetPassword(), true), button('Cancel', () => closeReset())),
          resetStatus.label
        )
      );

      // ---- list ----
      const countLine = muted('');
      const list = new CardList<HostUserDto>({ key: u => u.id, version: u => [u, me()], build: u => buildRow(u) });
      const listCard = card();
      listCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), countLine, list.panel));

      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), editor, resetCard, listCard));

      // ---------------------------------------------------------------

      function me(): HostUserDto | null {
        return hostAccess.current();
      }

      function buildRow(u: HostUserDto): Card {
        const self = u.id === currentUser()?.id;
        const actor = me();
        const changeable = !self && actor !== null && covers(actor, u);

        const heading = inlineRow();
        heading.controls.add(value(u.displayName, { font: FONT.bold }));
        if (u.permissions === HostPermission.All && u.allOrganizations) heading.controls.add(chip('Full access', 'brand'));
        if (u.disabled) heading.controls.add(chip('Disabled', 'danger'));
        if (u.mustChangePassword) heading.controls.add(chip('Must change password', 'warn'));
        if (self) heading.controls.add(chip('you', 'info'));

        const body = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          text(u.email, { font: FONT.small }),
          text(permissionSummary(u.permissions), { font: FONT.small }),
          text(organizationSummary(u), { font: FONT.small }),
          muted(join([u.lastSignInUtc ? 'Last signed in ' + localDateTime(u.lastSignInUtc) : 'Never signed in', 'added ' + localDate(u.createdUtc)]))
        );

        if (changeable) {
          const actions = buttonRow();
          actions.controls.add(button('Edit', () => openEditor(u)));
          actions.controls.add(button('Reset password', () => openReset(u)));
          actions.controls.add(button(u.disabled ? 'Enable' : 'Disable', () => void setDisabled(u, !u.disabled)));
          actions.controls.add(button('Remove', () => void remove(u)));
          body.controls.add(actions);
        } else if (self) {
          const actions = buttonRow();
          actions.controls.add(button('Rename', () => openEditor(u)));
          body.controls.add(actions);
          body.controls.add(muted('Another operator has to change your own access.'));
        } else {
          body.controls.add(muted('This operator has access you do not, so only they or an operator with more can change it.'));
        }

        const shell = card({ tint: u.disabled ? 'alt' : undefined });
        shell.controls.add(body);
        return shell;
      }

      /** The organizations the signed-in operator may hand out - the server lists only those. */
      function buildOrgRows(u: HostUserDto | null): void {
        orgRowsPanel.controls.clear();
        orgRows = tenants.map(t => {
          const assigned = u?.organizations?.find(o => o.tenantId === t.id);
          const check = wrappingCheck(t.name, assigned !== undefined);
          const role = comboBox(ROLES, {
            itemText: r => r.label,
            selectedIndex: assigned && !assigned.administrator ? 1 : 0,
            width: 150,
            hAlign: 'end'
          });
          const row = splitRow({ align: 'center', spacing: GAP.sm });
          fillWith(row, checkLine(check));
          row.controls.add(role);
          orgRowsPanel.controls.add(row);
          return { tenant: t, check, role };
        });
        // An operator limited to some organizations cannot make a technician an administrator where
        // they are only a technician themselves; the server refuses it too.
        const actor = me();
        for (const r of orgRows) {
          const mine = actor?.allOrganizations ? true : actor?.organizations?.find(o => o.tenantId === r.tenant.id)?.administrator;
          if (mine === false) {
            r.role.selectedIndex = 1;
            r.role.enabled = false;
          }
        }
      }

      function showOrgRows(): void {
        const all = allOrgs.checked;
        orgRowsPanel.visible = !all && orgRows.length > 0;
        orgHint.text = all
          ? 'They open every organization as an administrator.'
          : orgRows.length === 0
            ? 'There are no organizations you can assign.'
            : 'Tick the organizations they may open, and choose what they work as in each.';
        relayout(organizations);
      }

      function openEditor(u: HostUserDto | null): void {
        closeReset();
        editing = u;
        const self = u !== null && u.id === currentUser()?.id;
        editorTitle.text = u ? `Edit ${u.displayName}` : 'Add operator';
        saveButton.text = u ? 'Save' : 'Add';
        nameBox.text = u?.displayName ?? '';
        emailBox.text = u?.email ?? '';
        emailField.visible = u === null;
        passwordField.visible = u === null;
        tempPassword.text = '';

        const actor = me();
        for (const p of permChecks) {
          p.check.checked = u !== null && (u.permissions & p.flag) === p.flag;
          // Only what the signed-in operator holds may be handed out.
          p.check.enabled = actor !== null && (actor.permissions & p.flag) === p.flag;
        }
        allOrgs.checked = u?.allOrganizations ?? false;
        allOrgs.enabled = actor?.allOrganizations ?? false;
        buildOrgRows(u);
        // Their own access is someone else's to change; a rename is all that is offered.
        permissions.visible = !self;
        organizations.visible = !self;
        showOrgRows();

        editorStatus.clear();
        editor.visible = true;
        relayout(content);
        nameBox.focus();
      }

      function closeEditor(): void {
        editing = null;
        tempPassword.text = '';
        editor.visible = false;
        relayout(content);
      }

      function openReset(u: HostUserDto): void {
        closeEditor();
        resetting = u;
        resetTitle.text = `Reset the password of ${u.displayName}`;
        newPassword.text = '';
        confirmPassword.text = '';
        resetStatus.clear();
        resetCard.visible = true;
        relayout(content);
        newPassword.focus();
      }

      function closeReset(): void {
        resetting = null;
        newPassword.text = '';
        confirmPassword.text = '';
        resetCard.visible = false;
        relayout(content);
      }

      function chosenPermissions(): HostPermission {
        return permChecks.reduce((acc, p) => (p.check.checked ? acc | p.flag : acc), HostPermission.None);
      }

      function chosenOrganizations(): HostTenantAssignment[] {
        return orgRows.filter(r => r.check.checked).map(r => ({ tenantId: r.tenant.id, administrator: r.role.selectedItem?.admin ?? true }));
      }

      async function save(): Promise<void> {
        if (busy) return;
        const target = editing;
        const self = target !== null && target.id === currentUser()?.id;
        busy = true;
        editorStatus.busy(target ? 'Saving…' : 'Adding…');
        try {
          const all = allOrgs.checked;
          if (target) {
            const saved = await host.updateUser(
              target.id,
              self
                ? { displayName: nameBox.text.trim(), disabled: null, permissions: null, allOrganizations: null, organizations: null }
                : {
                    displayName: nameBox.text.trim(),
                    disabled: null,
                    permissions: chosenPermissions(),
                    allOrganizations: all,
                    organizations: all ? [] : chosenOrganizations()
                  }
            );
            if (ctx.cancelled()) return;
            status.ok(`Saved ${saved.displayName}. A session they have open inside an organization follows the change on its next request.`);
          } else {
            const added = await host.createUser({
              email: emailBox.text.trim(),
              displayName: nameBox.text.trim(),
              password: tempPassword.text,
              permissions: chosenPermissions(),
              allOrganizations: all,
              organizations: all ? [] : chosenOrganizations()
            });
            if (ctx.cancelled()) return;
            status.ok(`Added ${added.email}. Give them the temporary password; they choose their own at first sign-in.`);
          }
          closeEditor();
          await load();
        } catch (error) {
          // The form stays open with what was typed, so a refusal is fixed rather than retyped.
          if (!ctx.cancelled()) editorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function resetPassword(): Promise<void> {
        if (busy || !resetting) return;
        if (newPassword.text !== confirmPassword.text) {
          resetStatus.error('The two passwords do not match.');
          return;
        }
        busy = true;
        resetStatus.busy('Resetting…');
        try {
          const saved = await host.resetUserPassword(resetting.id, { password: newPassword.text });
          if (ctx.cancelled()) return;
          closeReset();
          status.ok(`Reset the password of ${saved.displayName}. Their sessions were signed out.`);
          await load();
        } catch (error) {
          if (!ctx.cancelled()) resetStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function setDisabled(u: HostUserDto, disabled: boolean): Promise<void> {
        if (disabled) {
          const ok = await confirm(ctx.desktop, {
            title: `Disable ${u.displayName}`,
            message: `${u.email} will be refused at sign-in, and their sessions end - including one open inside an organization, on its next request. Enable gives the account back.`
          });
          if (!ok || ctx.cancelled()) return;
        }
        await act(disabled ? 'Disabling…' : 'Enabling…', async () => {
          const saved = await host.updateUser(u.id, {
            displayName: null,
            disabled,
            permissions: null,
            allOrganizations: null,
            organizations: null
          });
          return `${saved.displayName} is ${saved.disabled ? 'disabled' : 'enabled'}.`;
        });
      }

      async function remove(u: HostUserDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: `Remove ${u.displayName}`,
          message: `Remove ${u.email}? Their sessions end and they can no longer sign in to the host. What they did stays in the host's activity and in each organization's audit log. Disable instead keeps the account for later.`
        });
        if (!ok || ctx.cancelled()) return;
        await act('Removing…', async () => {
          await host.deleteUser(u.id);
          return `Removed ${u.email}.`;
        });
      }

      async function act(working: string, work: () => Promise<string>): Promise<void> {
        if (busy) return;
        busy = true;
        status.busy(working);
        try {
          const done = await work();
          if (ctx.cancelled()) return;
          status.ok(done);
          await load();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function load(): Promise<void> {
        try {
          const signal = ctx.dispose.signal();
          const [us, ts] = await Promise.all([host.users(signal), host.tenants(signal)]);
          if (ctx.cancelled()) return;
          tenants = ts;
          const disabled = us.filter(u => u.disabled).length;
          const full = us.filter(u => !u.disabled && u.allOrganizations && u.permissions === HostPermission.All).length;
          countLine.text = join([
            plural(us.length, 'operator'),
            plural(full, 'with full access', 'with full access'),
            disabled > 0 ? `${disabled} disabled` : null
          ]);
          list.set(us);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the operators: ' + describeError(error));
        }
      }

      ctx.dispose.add(hostAccess.onChanged(() => void load()));
      void load();
    }
  };
}
